import { useMutation, useReactiveVar } from '@apollo/client';
import { Typography } from '@mui/material';
import type React from 'react';
import allFieldDefinitionsQuery from 'src/api/admin/allFieldDefinitionsQuery';
import deleteFieldDefMutation from 'src/api/admin/deleteFieldDefMutation';
import AkomiDialog from 'src/components/dialogs/AkomiDialog';
import AkomiDialogActions from 'src/components/dialogs/AkomiDialogActions';
import { useCustomToast } from 'src/hooks/menu';
import viewStateVar from 'src/store/viewState';

interface DeleteFieldDefMutationVariables {
  fieldDefId: string;
}

interface DeleteFieldDefMutationResponse {
  deleteFieldDef: {
    success: boolean;
  };
}

const FieldDefDeleteModal: React.FC = () => {
  const viewState = useReactiveVar(viewStateVar);
  const { toastr } = useCustomToast();

  const [deleteFieldDef] = useMutation<DeleteFieldDefMutationResponse, DeleteFieldDefMutationVariables>(
    deleteFieldDefMutation,
    {
      fetchPolicy: 'network-only',
      refetchQueries: [allFieldDefinitionsQuery],
    },
  );

  const deleteFieldItemHandler = (): void => {
    deleteFieldDef({
      variables: {
        fieldDefId: viewState.deleteFieldDefId,
      },
    })
      .then(() => {
        toastr.success(`Field '${viewState.deleteFieldDefName}' was deleted successfully`);
      })
      .catch((error: Error) => {
        toastr.error(`Field '${viewState.deleteFieldDefName}' cannot be deleted because ${error.message}`);
      });
    viewStateVar({
      ...viewStateVar(),
      fieldDefDeleteDialog: null,
    });
  };

  const cancelDeleteHandler = (): void => {
    viewStateVar({
      ...viewStateVar(),
      fieldDefDeleteDialog: null,
    });
  };

  return (
    <AkomiDialog
      title="Delete"
      maxWidth="xs"
      open={!!viewState.fieldDefDeleteDialog}
      onClose={cancelDeleteHandler}
      actions={
        <AkomiDialogActions
          variant="delete-cancel"
          primaryLabel="Delete"
          onPrimary={deleteFieldItemHandler}
          onSecondary={cancelDeleteHandler}
        />
      }
    >
      <Typography>
        Are you sure you want to delete &ldquo;{viewState.deleteFieldDefName}
        &rdquo;?
      </Typography>
    </AkomiDialog>
  );
};

export default FieldDefDeleteModal;
